import type { RouteConfig } from './index';

/**
 * 扁平化路由配置，生成路径到路由配置的映射
 */
function flattenRoutes(routeConfigs: RouteConfig[], parentPath = ''): Map<string, RouteConfig> {
  const routeMap = new Map<string, RouteConfig>();

  routeConfigs.forEach((route) => {
    // 构建完整路径
    let fullPath = route.path;
    if (parentPath && !route.path.startsWith('/')) {
      fullPath = `${parentPath}/${route.path}`;
    } else if (!route.path.startsWith('/') && route.path !== '') {
      fullPath = `/${route.path}`;
    }

    // 标准化路径（移除末尾斜杠，处理空路径）
    fullPath = fullPath.replace(/\/+/g, '/').replace(/\/$/, '') || '/';

    // 存储路由配置
    if (route.meta?.title) {
      routeMap.set(fullPath, route);
    }

    // 递归处理子路由
    if (route.children && route.children.length > 0) {
      const childMap = flattenRoutes(route.children, fullPath);
      childMap.forEach((value, key) => {
        routeMap.set(key, value);
      });
    }
  });

  return routeMap;
}

export type RouteConfigMap = Map<string, RouteConfig>;

export function buildRouteConfigMap(routeConfigs: RouteConfig[]): RouteConfigMap {
  return flattenRoutes(routeConfigs);
}

export function matchRouteConfig(
  path: string,
  routeConfigs: RouteConfig[],
  routeMap?: RouteConfigMap
): RouteConfig | undefined {
  const map = routeMap ?? flattenRoutes(routeConfigs);
  const normalizedPath = path.replace(/\/+/g, '/').replace(/\/$/, '') || '/';

  const exactRoute = map.get(normalizedPath);
  if (exactRoute) {
    return exactRoute;
  }

  const pathSegments = normalizedPath.split('/').filter(Boolean);

  for (const [routePath, routeConfig] of map.entries()) {
    const routeSegments = routePath.split('/').filter(Boolean);

    if (routeSegments.length !== pathSegments.length) {
      continue;
    }

    const isMatch = routeSegments.every((segment, index) => {
      if (segment.startsWith(':')) {
        return true;
      }
      return segment === pathSegments[index];
    });

    if (isMatch) {
      return routeConfig;
    }
  }

  return undefined;
}

/**
 * 根据路径获取路由标题
 */
export function getRouteTitle(
  path: string,
  routeConfigs: RouteConfig[],
  routeMap?: RouteConfigMap
): string | undefined {
  const map = routeMap ?? flattenRoutes(routeConfigs);

  // 标准化路径
  const normalizedPath = path.replace(/\/+/g, '/').replace(/\/$/, '') || '/';

  // 首先尝试精确匹配
  const route = map.get(normalizedPath);
  if (route?.meta?.title) {
    return route.meta.title;
  }

  // 如果是动态路由（包含参数），尝试匹配路由模式
  const pathSegments = normalizedPath.split('/').filter(Boolean);

  for (const [routePath, routeConfig] of map.entries()) {
    const routeSegments = routePath.split('/').filter(Boolean);

    // 路径段数量必须相同
    if (routeSegments.length !== pathSegments.length) {
      continue;
    }

    // 检查每个段是否匹配（考虑动态参数）
    const isMatch = routeSegments.every((segment, index) => {
      // 动态参数段（如 :id）匹配任何值
      if (segment.startsWith(':')) {
        return true;
      }
      // 普通段必须完全匹配
      return segment === pathSegments[index];
    });

    if (isMatch && routeConfig.meta?.title) {
      return routeConfig.meta.title;
    }
  }

  return undefined;
}

/**
 * 根据当前路径生成面包屑数据
 */
export interface BreadcrumbItem {
  label: string;
  href: string;
}

export function getBreadcrumbItems(
  pathname: string,
  routeConfigs: RouteConfig[],
  routeMap?: RouteConfigMap
): BreadcrumbItem[] {
  const map = routeMap ?? flattenRoutes(routeConfigs);
  const items: BreadcrumbItem[] = [];

  // 添加首页
  items.push({ label: '首页', href: '/' });

  // 如果是首页，直接返回
  if (pathname === '/' || pathname === '') {
    return items;
  }

  const pathSegments = pathname.split('/').filter(Boolean);

  // 逐级构建面包屑
  pathSegments.forEach((_, index) => {
    const path = '/' + pathSegments.slice(0, index + 1).join('/');
    const title = getRouteTitle(path, routeConfigs, map);

    if (title) {
      items.push({ label: title, href: path });
    }
  });

  return items;
}

/**
 * 获取第一个有权限的路由路径
 * 递归遍历路由配置，找到第一个有 element 且不是重定向的路由
 * @param routeConfigs 路由配置数组
 * @param parentPath 父路径，用于构建完整路径
 */
export function getFirstAuthorizedRoute(
  routeConfigs: RouteConfig[],
  parentPath = ''
): string | null {
  for (const route of routeConfigs) {
    // 跳过根路径和特殊路径
    if (
      route.path === '/' ||
      route.path === '/login' ||
      route.path === '/404' ||
      route.path === '*'
    ) {
      continue;
    }

    // 构建当前完整路径
    let currentPath = route.path;
    if (parentPath && !route.path.startsWith('/')) {
      currentPath = `${parentPath}/${route.path}`;
    } else if (!route.path.startsWith('/') && route.path !== '') {
      currentPath = `/${route.path}`;
    }
    // 标准化路径
    currentPath = currentPath.replace(/\/+/g, '/');

    // 如果有子路由，递归查找
    if (route.children && route.children.length > 0) {
      const childRoute = getFirstAuthorizedRoute(route.children, currentPath);
      if (childRoute) {
        return childRoute;
      }
    }

    // 如果当前路由有 element 且不是空路径，返回该路径
    if (route.element && route.path && route.path !== '') {
      return currentPath;
    }
  }

  return null;
}
